import { get } from "lodash-es";
import objectHash from "object-hash";
import React, { FC, useContext, useEffect, useState } from "react";
import { useIdToken } from "react-firebase-hooks/auth";
import { Navigate, Outlet, useLocation } from "react-router";

import AuthenticatedAnalyticsBootstrap from "../analytics/AuthenticatedAnalyticsBootstrap";
import LoadingScreen from "../components/LoadingScreen";
import { firebaseAuth } from "../configs/firebase";
import { UserContext } from "../contexts/UserContext";
import { setClusterAndOrg, setOrgId } from "../slices/current";
import { buildTree } from "../slices/namespaces";
import { indexOrganizations, setOrgClusters } from "../slices/organizations";
import { useDispatch, useSelector } from "../store";
import {
  useListClustersQuery,
  useListNamespacesQuery,
  useListOrganizationsQuery,
} from "../store/pomeriumApi";

export const AuthGuard: FC = () => {
  const dispatch = useDispatch();
  const { user: pomeriumUser } = useContext(UserContext);
  const [user, loading] = useIdToken(firebaseAuth);
  const { clusterId, organizationId } = useSelector((state) => state.current);
  const { byId, clustersByOrg } = useSelector((state) => state.organizations);
  const location = useLocation();
  const [localClustId, setLocalClustId] = useState("");
  const [localOrgId, setLocalOrgId] = useState("");

  const { data: organizations } = useListOrganizationsQuery(undefined, {
    skip: !pomeriumUser?.id,
  });
  const { data: namespaces } = useListNamespacesQuery(organizationId, {
    skip: !organizationId,
  });
  const { data: clusters } = useListClustersQuery(organizationId, {
    skip: !organizationId,
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const oid = params.get("oid");
    const cid = params.get("cid");
    if (pomeriumUser?.id) {
      const lastOid = localStorage.getItem(pomeriumUser?.id + "_organizationId");
      const lastCid = localStorage.getItem(pomeriumUser?.id + "_clusterId");
      // oxlint-disable-next-line react/set-state-in-effect -- ENG-4333: fix and remove this disable
      setLocalOrgId(oid || lastOid || "");
      setLocalClustId(cid || lastCid || "");
      localStorage.setItem(pomeriumUser?.id + "_organizationId", oid || lastOid || "");
      localStorage.setItem(pomeriumUser?.id + "_clusterId", cid || lastCid || "");
    }
  }, [location.search, pomeriumUser?.id]);

  useEffect(() => {
    dispatch(indexOrganizations(organizations || []));
  }, [JSON.stringify(organizations)]);

  useEffect(() => {
    if (organizationId) {
      dispatch(setOrgClusters({ organizationId, clusters: clusters || [] }));
    }
  }, [JSON.stringify(clusters || [])]);

  useEffect(() => {
    if (namespaces?.length && organizationId) {
      dispatch(buildTree(namespaces));
    }
  }, [JSON.stringify(namespaces), organizationId]);

  useEffect(() => {
    if (pomeriumUser?.id && organizations) {
      if (organizationId !== localOrgId && localOrgId && !!get(byId, localOrgId, null)) {
        dispatch(setOrgId(localOrgId));
      } else if (!organizationId || !get(byId, organizationId, null)) {
        dispatch(setOrgId(get(organizations, "0.id", "")));
      }
    }
  }, [localOrgId, objectHash(byId), pomeriumUser?.id]);

  useEffect(() => {
    const clusters = get(clustersByOrg, organizationId, null);
    if (organizationId && clusters) {
      const optimistic = clusters?.find((c) => c.id === localClustId)?.id || "";
      const backup = get(clusters, "0.id", "") as string;
      dispatch(
        setClusterAndOrg({
          clusterId: optimistic || backup,
          organizationId: organizationId,
        }),
      );
    }
  }, [localClustId, organizationId, objectHash(clustersByOrg)]);

  if (loading) {
    return <LoadingScreen />;
  }

  if (user && user?.emailVerified && pomeriumUser?.id) {
    const currentOrganization = organizations?.find(
      (organization) => organization.id === organizationId,
    );

    if (
      (organizationId &&
        !!get(byId, organizationId, null) &&
        clusterId &&
        get(clustersByOrg, organizationId, [])?.find((c) => c.id === clusterId)) ||
      organizations?.length === 0 ||
      (typeof clusters !== "undefined" && clusters?.length <= 1)
    ) {
      return (
        <>
          <AuthenticatedAnalyticsBootstrap
            firebaseUser={user}
            organization={currentOrganization}
            pomeriumUser={pomeriumUser}
          />
          <Outlet />
        </>
      );
    } else {
      return (
        <>
          <AuthenticatedAnalyticsBootstrap
            firebaseUser={user}
            organization={currentOrganization}
            pomeriumUser={pomeriumUser}
          />
          <LoadingScreen />
        </>
      );
    }
  }

  if (!user) {
    if (location.pathname.indexOf("logout") === -1) {
      localStorage.setItem("redirectUrl", location.pathname + location.search);
    }
    return <Navigate to="/login" />;
  }

  return <></>;
};
